// Firebase initialization — ai-quote 2.0 project (`ai-quote-2`).
//
// IMPORTANT: the `apiKey` here is NOT a secret. Per Firebase's design, web
// API keys identify the project but do NOT grant privileges — Security Rules
// + Firebase Auth are what actually gate read/write access. Safe to commit.
// See https://firebase.google.com/docs/projects/api-keys for the full doc.
//
// If you ever need to swap Firebase projects (dev vs prod, multi-shop), this
// is the single file to change — everywhere else in the codebase imports
// `db` / `auth` / `firebaseApp` from here.

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getDatabase, type Database } from 'firebase/database';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: 'AIzaSyABYOffjdYEk9rv9ZPG5179yxvrw4ywfWs',
  authDomain: 'ai-quote-2.firebaseapp.com',
  databaseURL: 'https://ai-quote-2-default-rtdb.firebaseio.com',
  projectId: 'ai-quote-2',
  storageBucket: 'ai-quote-2.firebasestorage.app',
  messagingSenderId: '93704775500',
  appId: '1:93704775500:web:5af2110fe3077634cfd1e9',
  measurementId: 'G-15LBCPSSZB',
} as const;

export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);
export const db: Database = getDatabase(firebaseApp);
export const auth: Auth = getAuth(firebaseApp);
export const storage: FirebaseStorage = getStorage(firebaseApp);

export const firebaseConfigured = true;

/**
 * Throw if Firebase isn't configured (sanity guard for modules that need it).
 * Reads/writes will fail at the Security Rules layer anyway, but this gives
 * a clearer error message at the call site.
 */
export function assertFirebaseConfigured(): void {
  if (!firebaseConfigured) {
    throw new Error(
      'Firebase not configured. See src/shared/firebase.ts for setup instructions.',
    );
  }
}

// =====================================================
// Auth — email/password sign-in
// =====================================================
//
// Email/password is the only enabled provider. There is NO anonymous fallback
// — the auth gate in App.tsx renders <LoginPage /> until someone is signed in
// and only then mounts the routes that actually call the SDK. awaitFirebaseUser()
// will sit forever if no one ever signs in, which is the desired behavior.
//
// Enable in Firebase Console → Authentication → Sign-in method → Email/Password.
// Then create accounts for each user (Justin + business partner) under
// Authentication → Users → Add user.

let _readyResolve: ((user: User) => void) | null = null;
let _firebaseUserReady: Promise<User> = new Promise((resolve) => {
  _readyResolve = resolve;
});

onAuthStateChanged(auth, (user) => {
  if (user) {
    if (_readyResolve) {
      _readyResolve(user);
      _readyResolve = null;
    }
    return;
  }
  // User signed out — reset the ready promise so the next sign-in is awaitable
  // by any caller that runs awaitFirebaseUser() AFTER the sign-out (e.g. a
  // post-sign-out route revisit). Without this, modules that already resolved
  // would keep returning the stale logged-out user.
  if (!_readyResolve) {
    _firebaseUserReady = new Promise((resolve) => {
      _readyResolve = resolve;
    });
  }
});

/**
 * Await Firebase auth readiness. Resolves with the current user once someone
 * is signed in. If no one is signed in, the promise sits forever — the auth
 * gate in App.tsx prevents SDK-calling components from mounting pre-auth, so
 * this is the right behavior (waiting at the SDK boundary catches any bug
 * that bypasses the gate).
 */
export function awaitFirebaseUser(): Promise<User> {
  // Already-signed-in shortcut so a re-render or background SDK call after
  // auth doesn't pay the promise-await round-trip.
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return _firebaseUserReady;
}

/** Current user synchronously, or null if not yet ready / signed out. */
export function currentFirebaseUser(): User | null {
  return auth.currentUser;
}

/**
 * Subscribe to auth state changes. Returns the unsubscribe fn — call it on
 * effect cleanup. Wraps Firebase's onAuthStateChanged so callers don't import
 * directly from firebase/auth.
 */
export function observeAuthState(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, cb);
}

/**
 * Translate Firebase Auth's raw error codes into friendlier messages. Firebase
 * surfaces things like "auth/invalid-credential" which is technically accurate
 * but unhelpful at a login form. The fallback returns the raw message so we
 * never lose information.
 */
function friendlyAuthError(err: unknown): Error {
  const code = (err as { code?: string })?.code ?? '';
  const message = (err as { message?: string })?.message ?? String(err);
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return new Error('Wrong email or password.');
    case 'auth/user-not-found':
      return new Error('No account with that email. Ask Justin to add you.');
    case 'auth/invalid-email':
      return new Error('That doesn\'t look like a valid email.');
    case 'auth/user-disabled':
      return new Error('This account is disabled. Ask Justin.');
    case 'auth/too-many-requests':
      return new Error('Too many failed attempts — wait a minute and try again.');
    case 'auth/email-already-in-use':
      return new Error('An account with that email already exists. Try signing in instead.');
    case 'auth/weak-password':
      return new Error('Password is too weak — use at least 6 characters.');
    case 'auth/network-request-failed':
      return new Error('Network error — check your connection and try again.');
    case 'auth/operation-not-allowed':
      return new Error(
        'Email/password sign-in is disabled in Firebase. Enable it in Firebase Console → Authentication → Sign-in method.',
      );
    default:
      return new Error(message);
  }
}

/**
 * Sign in with email + password. Throws a friendly Error on failure.
 * On success, onAuthStateChanged fires and the auth gate swaps to the app.
 */
export async function signInWithEmail(email: string, password: string): Promise<User> {
  try {
    const cred = await signInWithEmailAndPassword(auth, email.trim(), password);
    return cred.user;
  } catch (err) {
    throw friendlyAuthError(err);
  }
}

/**
 * Create a new email/password account. Used for first-run setup of additional
 * users. Returns the new User; the new account is automatically signed in.
 *
 * In practice Justin will create accounts via the Firebase Console for the
 * two-person team, so this is mostly a convenience for self-service onboarding
 * once the user base grows.
 */
export async function createAccountWithEmail(email: string, password: string): Promise<User> {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
    return cred.user;
  } catch (err) {
    throw friendlyAuthError(err);
  }
}

/** Sign out the current user. Auth gate flips back to LoginPage automatically. */
export async function signOut(): Promise<void> {
  try {
    await fbSignOut(auth);
  } catch (err) {
    throw friendlyAuthError(err);
  }
}
